import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { deviceProfilesApi, DeviceProfile, DeviceProfileBomItem } from '../api/accessoryShop';

const ROLES = ['relay', 'access-control-panel', 'reader', 'camera', 'intercom-panel', 'hub', 'doorbell', 'nvr', 'siren', 'ai-hub'];

interface BlankProfile {
  description: string;
  role: string;
  has_cellular: boolean;
  bom: DeviceProfileBomItem[];
  active: boolean;
  default_role_config: string; // JSON-as-text in form
}

const blank: BlankProfile = {
  description: '',
  role: 'relay',
  has_cellular: false,
  bom: [],
  active: true,
  default_role_config: '{}',
};

const computeName = (role: string, has_cellular: boolean) =>
  `bridge-${role}${has_cellular ? '-cellular' : ''}`;

export const DeviceProfilesAdmin: React.FC = () => {
  const [profiles, setProfiles] = useState<DeviceProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<DeviceProfile | null>(null);
  const [creating, setCreating] = useState<BlankProfile | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try { setProfiles(await deviceProfilesApi.list()); }
    catch (e: any) { setError(e?.message || 'Failed to load device profiles'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (data: BlankProfile) => {
    setSaving(true);
    setError(null);
    let parsedConfig: Record<string, unknown> = {};
    try {
      parsedConfig = data.default_role_config.trim() ? JSON.parse(data.default_role_config) : {};
    } catch {
      setError('default_role_config must be valid JSON');
      setSaving(false);
      return;
    }
    try {
      // name is generated server-side from role + has_cellular — never sent here.
      const payload = {
        description: data.description,
        role: data.role,
        has_cellular: data.has_cellular,
        bom: data.bom,
        active: data.active,
        default_role_config: parsedConfig,
      };
      if (editing) await deviceProfilesApi.update(editing.id, payload);
      else await deviceProfilesApi.create(payload);
      setEditing(null); setCreating(null);
      await load();
    } catch (e: any) { setError(e?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (p: DeviceProfile) => {
    if (!window.confirm(`Delete profile "${p.name}"?`)) return;
    try { await deviceProfilesApi.remove(p.id); await load(); }
    catch (e: any) { setError(e?.message || 'Delete failed'); }
  };

  const form = editing || creating;
  const formData: BlankProfile = editing ? {
    description: editing.description || '',
    role: editing.role,
    has_cellular: editing.has_cellular,
    bom: editing.bom || [],
    active: editing.active,
    default_role_config: JSON.stringify(editing.default_role_config || {}, null, 2),
  } : (creating || blank);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Bridge device profiles</h1>
          <p className="text-gray-500 mt-1">Per-SKU manufacturing templates: role, cellular flag, pick-list BOM, default role config.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={load} loading={loading}>Refresh</Button>
          <Button size="sm" onClick={() => setCreating(blank)}>+ New profile</Button>
        </div>
      </div>

      {error && <div className="max-w-7xl mx-auto mb-4"><Card><div className="text-red-700 text-sm">{error}</div></Card></div>}

      <div className="max-w-7xl mx-auto">
        {loading && <Card><div className="text-gray-500 text-center py-4">Loading…</div></Card>}
        {!loading && profiles.length === 0 && (
          <Card><div className="text-gray-500 text-center py-6">No profiles yet — click "+ New profile".</div></Card>
        )}
        {!loading && profiles.length > 0 && (
          <Card padding={false}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs uppercase text-gray-500">
                    <th className="p-3">Name</th>
                    <th className="p-3">Role</th>
                    <th className="p-3">Cellular</th>
                    <th className="p-3 text-right">BOM items</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {profiles.map(p => (
                    <tr key={p.id}>
                      <td className="p-3">
                        <div className="font-medium text-gray-900">{p.name}</div>
                        <div className="text-xs text-gray-500 line-clamp-1">{p.description}</div>
                      </td>
                      <td className="p-3"><Badge color="gray">{p.role}</Badge></td>
                      <td className="p-3">{p.has_cellular ? <Badge color="blue">Cellular</Badge> : <span className="text-gray-400 text-xs">—</span>}</td>
                      <td className="p-3 text-right">{(p.bom || []).reduce((s, b) => s + b.qty, 0)} ({(p.bom || []).length} skus)</td>
                      <td className="p-3">{p.active ? <Badge color="green">Active</Badge> : <Badge color="gray">Inactive</Badge>}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(p)}>Delete</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {form && (
        <ProfileForm
          initial={formData}
          isCreate={!editing}
          saving={saving}
          onCancel={() => { setEditing(null); setCreating(null); }}
          onSubmit={save}
        />
      )}
    </div>
  );
};

const ProfileForm: React.FC<{
  initial: BlankProfile;
  isCreate: boolean;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (d: BlankProfile) => void;
}> = ({ initial, isCreate, saving, onCancel, onSubmit }) => {
  const [d, setD] = useState<BlankProfile>(initial);
  const valid = !!d.role;
  const previewName = computeName(d.role, d.has_cellular);

  const updateBomRow = (idx: number, patch: Partial<DeviceProfileBomItem>) => {
    setD(prev => ({ ...prev, bom: prev.bom.map((r, n) => n === idx ? { ...r, ...patch } : r) }));
  };
  const addBomRow = () => setD(prev => ({ ...prev, bom: [...prev.bom, { sku: '', label: '', qty: 1, location: '' }] }));
  const removeBomRow = (idx: number) => setD(prev => ({ ...prev, bom: prev.bom.filter((_, n) => n !== idx) }));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">{isCreate ? 'New device profile' : 'Edit device profile'}</h2>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Role *</label>
              <select className="w-full border rounded px-3 py-2 text-sm" value={d.role} onChange={e => setD({ ...d, role: e.target.value })}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={d.has_cellular} onChange={e => setD({ ...d, has_cellular: e.target.checked })} />
              Has cellular modem — provisioned with cellular as primary network, Wi-Fi fallback
            </label>

            <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm">
              <div className="text-xs text-gray-500 mb-1">Profile name (auto-generated)</div>
              <code className="text-gray-900 font-mono">{previewName}</code>
              <div className="text-xs text-gray-400 mt-1">A numeric suffix is appended automatically if this name is already taken.</div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea rows={2} className="w-full border rounded px-3 py-2 text-sm" value={d.description} onChange={e => setD({ ...d, description: e.target.value })} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-600">Bill of materials (warehouse pick list)</label>
                <Button size="sm" variant="ghost" onClick={addBomRow}>+ Add item</Button>
              </div>
              {d.bom.length === 0 ? (
                <div className="text-xs text-gray-400 border border-dashed border-gray-300 rounded p-3 text-center">
                  No BOM items yet — add Pi, microSD, case, PSU, etc.
                </div>
              ) : (
                <div className="border border-gray-200 rounded divide-y">
                  {d.bom.map((row, idx) => (
                    <div key={idx} className="p-2 grid grid-cols-12 gap-2 items-center">
                      <input className="col-span-3 border rounded px-2 py-1 text-xs" placeholder="SKU" value={row.sku} onChange={e => updateBomRow(idx, { sku: e.target.value })} />
                      <input className="col-span-5 border rounded px-2 py-1 text-xs" placeholder="Label (e.g. Pi 5 4GB)" value={row.label} onChange={e => updateBomRow(idx, { label: e.target.value })} />
                      <input type="number" min={1} className="col-span-1 border rounded px-2 py-1 text-xs text-right" value={row.qty} onChange={e => updateBomRow(idx, { qty: Math.max(1, Number(e.target.value) || 1) })} />
                      <input className="col-span-2 border rounded px-2 py-1 text-xs" placeholder="Bin / shelf" value={row.location || ''} onChange={e => updateBomRow(idx, { location: e.target.value })} />
                      <button onClick={() => removeBomRow(idx)} className="col-span-1 text-red-500 hover:text-red-700 text-sm">✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Default role config (JSON) <span className="text-gray-400">— shadow baked into device on provision</span>
              </label>
              <textarea
                rows={6}
                className="w-full border rounded px-3 py-2 text-xs font-mono"
                placeholder='{"driver":"gpio","channels":[{"channel":1,"gpioPin":17}]}'
                value={d.default_role_config}
                onChange={e => setD({ ...d, default_role_config: e.target.value })}
              />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={d.active} onChange={e => setD({ ...d, active: e.target.checked })} />
              Active
            </label>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={onCancel}>Cancel</Button>
            <Button onClick={() => onSubmit(d)} loading={saving} disabled={!valid}>
              {isCreate ? 'Create profile' : 'Save changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
